/* eslint-disable max-lines */
import { UnknownAction } from "@reduxjs/toolkit";
import { z } from "zod";
import { PaginationSchema } from "@/components/paginate/types";
import { UserSchema } from "@/types";

export type EntityType = "rule" | "user" | "usergroup" | "ownership";

export enum UserGroupEntities {
  USER = "user",
  ROLE = "role",
  WAREHOUSE = "warehouse",
  COMPUTE_POOL = "compute_pool",
  DATABASE = "database",
  SCHEMA = "schema",
  QUERY_TAG = "query_tag",
  DATASTORE = "datastore",
  DBT_INTEGRATION = "dbt_integration",
  // Databricks access-control entity types. The backend converter routes these
  // to the "databricks" provider and enforces them on account_id / workspace_id.
  ACCOUNT = "account",
  WORKSPACE = "workspace",
}

export const UserGroupEntitiesLabelsAndValues: { label: string; value: UserGroupEntities }[] = [
  {
    label: "user",
    value: UserGroupEntities.USER,
  },
  {
    label: "role",
    value: UserGroupEntities.ROLE,
  },
  {
    label: "warehouse",
    value: UserGroupEntities.WAREHOUSE,
  },
  {
    label: "database",
    value: UserGroupEntities.DATABASE,
  },
  {
    label: "schema",
    value: UserGroupEntities.SCHEMA,
  },
  {
    label: "query_tag",
    value: UserGroupEntities.QUERY_TAG,
  },
  {
    label: "instance",
    value: UserGroupEntities.DATASTORE,
  },
  {
    label: "dbt integration",
    value: UserGroupEntities.DBT_INTEGRATION,
  },
];

// Databricks tenants scope teams by account / workspace instead of the
// Snowflake entity types above.
export const DatabricksUserGroupEntitiesLabelsAndValues: {
  label: string;
  value: UserGroupEntities;
}[] = [
  {
    label: "account",
    value: UserGroupEntities.ACCOUNT,
  },
  {
    label: "workspace",
    value: UserGroupEntities.WORKSPACE,
  },
];

export enum UserGroupRuleTypes {
  EXACT = "exact",
  PREFIX = "starts_with",
  SUFFIX = "ends_with",
  CONTAINS = "contains",
  IN = "in",
}

// Ownership-specific enums
export enum OwnershipEntities {
  WAREHOUSE = "warehouse",
  COMPUTE_POOL = "compute_pool",
  QUERY = "query",
  TABLE = "table",
  DATABASE = "database",
  SCHEMA = "schema",
}

export enum OwnershipConditionKeys {
  // Warehouse
  WAREHOUSE_NAME = "name",
  WAREHOUSE_TAG = "tag",

  // Query
  QUERY_WAREHOUSE_NAME = "warehouse_name",
  QUERY_USER = "user",
  QUERY_ROLE = "role",
  QUERY_TAG = "tag",

  // Table
  TABLE_NAME = "name",

  // Database
  DATABASE_NAME = "name",

  // Schema
  SCHEMA_NAME = "name",
}

export enum OwnershipConditionTypes {
  EQUALS = "equals",
  STARTS_WITH = "startWith",
  CONTAINS = "contains",
  IN = "in",
  TAG = "tag", // Special type for key:value pairs
}

export const UserGroupRulesZodSchema = z.object({
  id: z.coerce.string(),
  entity_type: z.nativeEnum(UserGroupEntities),
  rule: z.object({
    entity: z.string(),
    type: z.nativeEnum(UserGroupRuleTypes),
  }),
  created_at: z.string(),
  created_by: z.string(),
  updated_at: z.string(),
  updated_by: z.string(),
  grouping: z.string().optional(),
});

export interface UserGroupRules extends z.infer<typeof UserGroupRulesZodSchema> {}

// Ownership-related schemas
export const UserGroupOwnershipZodSchema = z.object({
  id: z.coerce.string(),
  entity_type: z.nativeEnum(OwnershipEntities),
  ownership_rule: z.object({
    condition_key: z.nativeEnum(OwnershipConditionKeys),
    condition_type: z.nativeEnum(OwnershipConditionTypes),
    condition_value: z.string(),
    condition_key_value: z.string().optional(),
  }),
  is_deleted: z.boolean(),
  is_active: z.boolean(),
  user_group_id: z.number(),
  created_at: z.string(),
  created_by: z.string(),
  updated_at: z.string(),
  updated_by: z.string(),
  grouping: z.string().optional(),
});

export interface UserGroupOwnership extends z.infer<typeof UserGroupOwnershipZodSchema> {}

export const UserGroupZodSchema = z.object({
  id: z.coerce.string(),
  uuid: z.string(),
  name: z.string(),
  description: z.string(),
  symbol: z.string().optional(),
  is_owner: z.boolean().optional(),
  created_at: z.string().optional(),
  created_by: z.coerce.string(),
  updated_at: z.string().optional(),
  updated_by: z.coerce.string(),
  has_conflicts: z.boolean().nullable().optional(),
});

export interface UserGroup extends z.infer<typeof UserGroupZodSchema> {}

export const UserGroupWithMembersAndRulesZodSchema = z.object({
  ...UserGroupZodSchema.shape,
  users: z.array(UserSchema),
  user_group_rules: z.array(UserGroupRulesZodSchema),
  user_group_ownership: z.array(UserGroupOwnershipZodSchema).optional(),
  is_deleted: z.boolean().optional(),
});

export interface UserGroupWithMembersAndRules extends z.infer<
  typeof UserGroupWithMembersAndRulesZodSchema
> {}

export interface UserGroupsStateProps {
  selectedUserGroup?: string;
  userGroups: UserGroup[];
}

export interface ContextProps {
  state: UserGroupsStateProps;
  dispatch: React.Dispatch<UnknownAction>;
}

export const GetAllUserGroupsResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(UserGroupZodSchema),
});

export const UploadUserResponseSchema = z.object({
  ok: z.boolean(),
  failures: z.array(
    z.object({
      email: z.string(),
      group_name: z.string(),
      reason: z.string(),
    })
  ),
  success: z.array(
    z.object({
      email: z.string(),
      group_name: z.string(),
    })
  ),
});

export type UploadUserResponse = z.infer<typeof UploadUserResponseSchema>;

export const UploadUserGroupResponseSchema = z.object({
  ok: z.boolean(),
  failures: z.array(
    z.object({
      entity_type: z.string(),
      group_name: z.string(),
      reason: z.string(),
      match_type: z.string(),
    })
  ),
  success: z.array(
    z.object({
      entity_type: z.string(),
      group_name: z.string(),
      match_type: z.string(),
      description: z.string(),
      entity: z.string(),
    })
  ),
});

export type UploadUserGroupResponse = z.infer<typeof UploadUserGroupResponseSchema>;

// Form schemas
export const AccessRuleSchema = z.object({
  entity_type: z.nativeEnum(UserGroupEntities),
  match_type: z.nativeEnum(UserGroupRuleTypes),
  entity: z.string().min(1, "Entity is required"),
  key: z.string().optional(),
});

export const AccessRulesFormSchema = z
  .object({
    rules: z.array(AccessRuleSchema).min(1, "At least one rule is required"),
  })
  .refine(
    data => {
      // For query tags, key is required
      return data.rules.every(rule => {
        if (rule.entity_type === UserGroupEntities.QUERY_TAG) {
          return rule.key && rule.key.trim() !== "";
        }
        return true;
      });
    },
    {
      message: "Key is required for query tag rules",
      path: ["rules"],
    }
  );

export const OwnershipRuleSchema = z.object({
  entity_type: z.nativeEnum(OwnershipEntities),
  condition_key: z.nativeEnum(OwnershipConditionKeys),
  condition_type: z.nativeEnum(OwnershipConditionTypes),
  condition_value: z.string().min(1, "Value is required"),
  condition_key_value: z.string().optional(),
});

export const OwnershipRulesFormSchema = z
  .object({
    ownerships: z.array(OwnershipRuleSchema).min(1, "At least one ownership rule is required"),
  })
  .refine(
    data => {
      // For tag types, key value is required
      return data.ownerships.every(ownership => {
        if (ownership.condition_type === OwnershipConditionTypes.TAG) {
          return ownership.condition_key_value && ownership.condition_key_value.trim() !== "";
        }
        return true;
      });
    },
    {
      message: "Key is required for tag ownership rules",
      path: ["ownerships"],
    }
  );

export type AccessRulesFormValues = z.infer<typeof AccessRulesFormSchema>;
export type OwnershipRulesFormValues = z.infer<typeof OwnershipRulesFormSchema>;

// API Response Schemas
export const EntityCoverageSchema = z.object({
  entity_type: z.string(),
  total: z.number(),
  assigned_to_team: z.number(),
  unassigned: z.number(),
});

export interface EntityCoverage extends z.infer<typeof EntityCoverageSchema> {}

export const AccessRuleCoveragePreviewResponseSchema = z.object({
  coverage: z.array(EntityCoverageSchema),
});

export interface AccessRuleCoveragePreviewResponse extends z.infer<
  typeof AccessRuleCoveragePreviewResponseSchema
> {}

export const OwnershipCostPreviewResponseSchema = z.object({
  amount: z.number(),
  currency: z.string(),
  conflicted_entities_count: z.number(),
  conflicted_amount: z.number(),
});

export interface OwnershipCostPreviewResponse extends z.infer<
  typeof OwnershipCostPreviewResponseSchema
> {}

export const OwnershipConflictRuleSchema = z.object({
  entity_type: z.string(),
  condition_key: z.string(),
  condition_type: z.string(),
  condition_value: z.string(),
  condition_key_value: z.string().optional(),
});

export const OwnershipConflictDetailSchema = z.object({
  team_id: z.string(),
  team_name: z.string(),
  conflicting_rule: OwnershipConflictRuleSchema,
  conflicting_entity_count: z.number(),
  conflicting_amount: z.number(),
});

export interface OwnershipConflictDetail extends z.infer<typeof OwnershipConflictDetailSchema> {}

export const OwnershipConflictsResponseSchema = z.object({
  conflicts: z.array(OwnershipConflictDetailSchema),
  currency: z.string(),
});

export interface OwnershipConflictsResponse extends z.infer<
  typeof OwnershipConflictsResponseSchema
> {}

export const ConflictingTeamDetailSchema = z.object({
  team_list: z.array(z.string()),
  conflicting_entity_count: z.number(),
  conflicting_cost: z.number(),
});

export interface ConflictingTeamDetail extends z.infer<typeof ConflictingTeamDetailSchema> {}

export const ConflictingTeamsResponseSchema = z.object({
  conflicts: z.array(ConflictingTeamDetailSchema),
  currency: z.string(),
});

export interface ConflictingTeamsResponse extends z.infer<typeof ConflictingTeamsResponseSchema> {}

export const TeamConflictsResponseSchema = z.object({
  conflicted_team_count: z.number(),
  conflicted_entity_count: z.number(),
  conflicted_amount: z.number(),
  currency: z.string(),
  conflicted_teams: z.array(z.string()).optional(),
});

export interface TeamConflictsResponse extends z.infer<typeof TeamConflictsResponseSchema> {}

// Detailed team conflict for table display
export const TeamConflictDetailSchema = z.object({
  team_name: z.string(),
  entity_type: z.string(),
  entity_name: z.string(),
  warehouse_name: z.string().nullable().optional(),
  query_tag: z.string().nullable().optional(),
  database: z.string().nullable().optional(),
  schema: z.string().nullable().optional(),
  query_cost: z.number().optional().default(0),
});

export interface TeamConflictDetail extends z.infer<typeof TeamConflictDetailSchema> {}

export const TeamConflictDetailsResponseSchema = z.object({
  conflicts: z.array(TeamConflictDetailSchema),
  currency: z.string(),
  total: z.number(),
  page: z.number(),
  size: z.number(),
});

export interface TeamConflictDetailsResponse extends z.infer<
  typeof TeamConflictDetailsResponseSchema
> {}

// Ownership request payload types for API calls
export interface OwnershipRulePayload {
  entity_type: string;
  condition_key: string;
  condition_type: string;
  condition_value: string;
  condition_key_value?: string;
}

export interface OwnershipRequestPayload {
  entity_type: string;
  ownership_rules: OwnershipRulePayload[];
}

export interface OwnershipRequestsData extends Record<string, unknown> {
  ownership_requests: OwnershipRequestPayload[];
}
